import { useCallback, useEffect, useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import api, { stageClass, errMsg } from '../lib/api';
import { useAuth } from '../lib/AuthContext';
import { Input } from '../components/ui/input';
import { Button } from '../components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Popover, PopoverContent, PopoverTrigger } from '../components/ui/popover';
import { Calendar } from '../components/ui/calendar';
import { Plus, Search, X, CalendarIcon } from 'lucide-react';
import ClientFormDialog from '../components/ClientFormDialog';
import HelpTip from '../components/HelpTip';
import { toast } from 'sonner';
import { fmtDate } from '../lib/date';

// Inline calendar editor for the "Added on" column. Click the cell → Popover
// with a Calendar. Picking a date PATCHes referral_date and refreshes the row
// so the list resorts (newest-first) live. Stops the row's onClick so the
// full-edit dialog doesn't also open.
function AddedOnCell({ client, canEdit, onPatched }) {
  const [open, setOpen] = useState(false);
  const val = client.referral_date;
  const label = val ? fmtDate(val) : '—';
  if (!canEdit) return <span className="text-[#685B72]">{label}</span>;
  const pickDate = async (d) => {
    if (!d) return;
    const iso = d.toISOString().slice(0, 10);
    if (iso === (val || '').slice(0, 10)) { setOpen(false); return; }
    try {
      await api.patch(`/clients/${client.client_id}`, { referral_date: iso });
      onPatched?.({ ...client, referral_date: iso });
      toast.success(`Added-on set to ${iso}`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Update failed');
    }
    setOpen(false);
  };
  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button
          onClick={(e) => e.stopPropagation()}
          className="inline-flex items-center gap-1 rounded-md px-1.5 py-0.5 hover:bg-[#F1EDF5] text-[#685B72]"
          data-testid={`added-on-${client.client_id}`}
        >
          <CalendarIcon size={12} className="text-[#685B72]/70" />
          {label}
        </button>
      </PopoverTrigger>
      <PopoverContent align="start" className="w-auto p-0" onClick={(e) => e.stopPropagation()}>
        <Calendar
          mode="single"
          selected={val ? new Date(val) : undefined}
          onSelect={pickDate}
          initialFocus
        />
      </PopoverContent>
    </Popover>
  );
}

// Canonical stage list — must stay in sync with server.compute_stage.
// Building the dropdown from this instead of the filtered `clients` prevents the
// "picking a stage collapses the dropdown to only that stage" trap.
const ALL_STAGES = [
  'Referral Received',
  'Paperwork Pending',
  'Ready for Eval',
  'Pre-Auth Needed',
  'Pre-Auth Pending',
  'Eval Scheduled',
  'Eval Completed',
  'Ready for Therapy',
  'Therapy Scheduled',
  'Therapy Started',
  'Waitlisted',
  'On Hold',
  'On Hold — Pre-Auth Pending',
  'Inactive — Lost',
  'Inactive — Withdrew',
  'Inactive — Not Eligible',
  'Inactive — Financial/Ins Issue',
  'Inactive — Discharged',
];

export default function Pipeline() {
  const { user } = useAuth();
  const [searchParams] = useSearchParams();
  const [clients, setClients] = useState([]);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState('');
  const [clinic, setClinic] = useState(searchParams.get('clinic') || 'all');
  const [stage, setStage] = useState(searchParams.get('stage') || 'all');
  const [hideActioned, setHideActioned] = useState(searchParams.get('hide_actioned') === '1');
  const [setup, setSetup] = useState({});
  const [selectedClient, setSelectedClient] = useState(null);
  const [dialogOpen, setDialogOpen] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    const params = {};
    if (clinic !== 'all') params.clinic = clinic;
    if (stage !== 'all') params.stage = stage;
    if (q) params.q = q;
    if (hideActioned) params.hide_actioned = true;
    const r = await api.get('/clients', { params });
    setClients(r.data);
    setLoading(false);
  }, [q, clinic, stage, hideActioned]);

  useEffect(() => { api.get('/setup').then(r => setSetup(r.data || {})); }, []);
  useEffect(() => {
    const t = setTimeout(load, 250);
    return () => clearTimeout(t);
  }, [load]);

  // Deep-link: `/pipeline?open=<client_id>` (used by the Inquiries → Convert
  // flow). Fetch that client on mount and open the ClientFormDialog straight
  // away so admins land inside the new profile to finish filling it in.
  const openClientId = searchParams.get('open');
  useEffect(() => {
    if (!openClientId) return;
    let cancelled = false;
    api.get(`/clients/${openClientId}`)
      .then(r => {
        if (cancelled) return;
        setSelectedClient(r.data);
        setDialogOpen(true);
      })
      .catch(e => toast.error(errMsg(e, 'Could not open the converted client — refresh and try again.')));
    return () => { cancelled = true; };
  }, [openClientId]);

  const stages = useMemo(() => {
    // Union of canonical list and any stage seen in the current result set — so if the
    // engine emits a new stage we didn't hardcode, it still appears without a code change.
    const seen = new Set(clients.map(c => c.stage).filter(Boolean));
    ALL_STAGES.forEach(s => seen.add(s));
    return Array.from(seen).sort();
  }, [clients]);

  // iter 70 — user directive: every non-pending role has full R/W to the
  // client modal ("all client related info"). Adding `read_only` here
  // flips the modal from readOnly=true to fully editable for that role.
  const canEdit = ['super_user', 'admin', 'hr_payroll', 'office_director', 'read_only'].includes(user?.role);
  const canSeeAllClinics = ['super_user', 'admin', 'hr_payroll', 'read_only'].includes(user?.role);

  const handleSave = async (payload) => {
    try {
      if (selectedClient?.client_id) {
        const r = await api.patch(`/clients/${selectedClient.client_id}`, payload);
        setClients(prev => prev.map(c => c.client_id === r.data.client_id ? r.data : c));
        toast.success('Client updated');
      } else {
        const r = await api.post('/clients', payload);
        setClients(prev => [r.data, ...prev]);
        toast.success('Client created');
      }
      setDialogOpen(false);
    } catch (e) {
      toast.error(errMsg(e, 'Save failed'));
    }
  };

  const handleDelete = async (c) => {
    try {
      await api.delete(`/clients/${c.client_id}`);
      setClients(prev => prev.filter(x => x.client_id !== c.client_id));
      setDialogOpen(false);
      toast.success(`${c.client_name || 'Client'} deleted`);
    } catch (e) {
      toast.error(errMsg(e, 'Delete failed'));
    }
  };

  return (
    <div className="space-y-6" data-testid="pipeline-page">
      <div className="flex flex-wrap items-baseline justify-between gap-3">
        <div>
          <div className="text-xs uppercase tracking-widest text-[#685B72]/70 font-semibold">Referral Pipeline</div>
          <h1 className="font-serif text-4xl text-[#2D2833] mt-1">Client Pipeline</h1>
          <p className="text-[#685B72] mt-1">{clients.length.toLocaleString()} clients shown.</p>
        </div>
        {canEdit && (
          <Button onClick={() => { setSelectedClient(null); setDialogOpen(true); }} className="bg-[#685B72] hover:bg-[#52475A] text-white rounded-xl" data-testid="add-client-btn">
            <Plus size={16} className="mr-2" /> Add Client
          </Button>
        )}
      </div>

      <div className="bg-white rounded-2xl border border-[#E8E5EC] p-4 flex flex-wrap items-center gap-3">
        <div className="relative flex-1 min-w-[240px]">
          <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#685B72]" />
          <Input placeholder="Search by client name…" value={q} onChange={e => setQ(e.target.value)} className="pl-9 rounded-xl border-[#E8E5EC]" data-testid="pipeline-search" />
        </div>
        {canSeeAllClinics && (
          <div className="flex items-center gap-1">
            <Select value={clinic} onValueChange={setClinic}>
              <SelectTrigger className="w-[180px] rounded-xl" data-testid="filter-clinic"><SelectValue placeholder="All clinics" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All clinics</SelectItem>
                {(setup.clinics || []).map(c => <SelectItem key={c} value={c}>{c}</SelectItem>)}
              </SelectContent>
            </Select>
            {clinic !== 'all' && (
              <button onClick={() => setClinic('all')} className="h-9 w-9 flex items-center justify-center rounded-full text-[#685B72] hover:bg-[#F1EDF5]" data-testid="clear-clinic-filter" title="Clear clinic filter"><X size={14} /></button>
            )}
          </div>
        )}
        <div className="flex items-center gap-1">
          <Select value={stage} onValueChange={setStage}>
            <SelectTrigger className="w-[220px] rounded-xl" data-testid="filter-stage"><SelectValue placeholder="All stages" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All stages</SelectItem>
              {stages.map(s => <SelectItem key={s} value={s}>{s}</SelectItem>)}
            </SelectContent>
          </Select>
          {stage !== 'all' && (
            <button onClick={() => setStage('all')} className="h-9 w-9 flex items-center justify-center rounded-full text-[#685B72] hover:bg-[#F1EDF5]" data-testid="clear-stage-filter" title="Clear stage filter"><X size={14} /></button>
          )}
        </div>
        {(clinic !== 'all' || stage !== 'all' || q) && (
          <button onClick={() => { setClinic('all'); setStage('all'); setQ(''); }} className="text-xs text-[#685B72] hover:text-[#2D2833] underline underline-offset-2" data-testid="clear-all-filters">Clear all</button>
        )}
        <label className="ml-auto flex items-center gap-2 text-[12px] cursor-pointer select-none" data-testid="hide-actioned-toggle">
          <input
            type="checkbox"
            checked={hideActioned}
            onChange={(e) => setHideActioned(e.target.checked)}
            className="h-4 w-4 rounded border-[#BAA9CA] accent-[#685B72]"
            data-testid="hide-actioned-checkbox"
          />
          <span className="text-[#685B72]">Hide Therapy Started / Withdrew / Lost / On Hold</span>
          <HelpTip title="What gets hidden" testid="help-hide-actioned">
            Clients whose work is complete for admin — anyone in <b>Therapy Started</b> or <b>Therapy Scheduled</b>, <b>Waitlisted</b>, <b>On Hold</b>, or with a terminal outcome (<b>Lost</b>, <b>Withdrew</b>, <b>Not Eligible</b>, <b>Financial/Ins Issue</b>, <b>Discharged</b>). Turn on to focus only on rows still needing action.
          </HelpTip>
        </label>
      </div>

      <div className="bg-white rounded-2xl border border-[#E8E5EC] overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm" data-testid="pipeline-table">
            <thead>
              <tr className="bg-[#FAF8FB] text-[#685B72] text-left">
                <th className="px-4 py-3 font-semibold">Client</th>
                <th className="px-4 py-3 font-semibold">
                  <span className="inline-flex items-center gap-1">
                    Stage
                    <HelpTip title="Derived Stage" testid="help-stage">
                      Stages are computed automatically from paperwork, pre-auth, and scheduling. You <b>never manually pick</b> a stage — complete the underlying step (upload paperwork, submit pre-auth, book eval, etc.) and the stage updates on its own.
                    </HelpTip>
                  </span>
                </th>
                <th className="px-4 py-3 font-semibold">Added on</th>
                <th className="px-4 py-3 font-semibold">Clinic</th>
                <th className="px-4 py-3 font-semibold">Service</th>
                <th className="px-4 py-3 font-semibold">Referral</th>
                <th className="px-4 py-3 font-semibold">Insurance</th>
                <th className="px-4 py-3 font-semibold">Evaluator</th>
                <th className="px-4 py-3 font-semibold">Therapist</th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr><td colSpan={9} className="px-4 py-10 text-center text-[#685B72]">Loading…</td></tr>
              ) : clients.length === 0 ? (
                <tr><td colSpan={9} className="px-4 py-10 text-center text-[#685B72]">No clients match your filters.</td></tr>
              ) : clients.map(c => (
                <tr key={c.client_id} className="border-t border-[#E8E5EC] hover:bg-[#FAF8FB] cursor-pointer" data-testid={`client-row-${c.client_id}`} onClick={() => { setSelectedClient(c); setDialogOpen(true); }}>
                  <td className="px-4 py-3 font-medium text-[#2D2833]">{c.client_name}</td>
                  <td className="px-4 py-3"><span className={`stage-badge ${stageClass(c.stage)}`}>{c.stage || '—'}</span></td>
                  <td className="px-4 py-3" onClick={(e) => e.stopPropagation()}>
                    <AddedOnCell client={c} canEdit={canEdit} onPatched={(next) => setClients(prev => prev.map(x => x.client_id === next.client_id ? next : x))} />
                  </td>
                  <td className="px-4 py-3 text-[#685B72]">{c.clinic || '—'}</td>
                  <td className="px-4 py-3 text-[#685B72]">{c.service_type || '—'}</td>
                  <td className="px-4 py-3 text-[#685B72]">{c.referral_source || '—'}</td>
                  <td className="px-4 py-3 text-[#685B72]">{c.insurance_type || '—'}</td>
                  <td className="px-4 py-3 text-[#685B72]">{c.evaluator || '—'}</td>
                  <td className="px-4 py-3 text-[#685B72]">{c.therapist_assigned || '—'}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      <ClientFormDialog
        open={dialogOpen}
        onOpenChange={setDialogOpen}
        client={selectedClient}
        setup={setup}
        onSave={handleSave}
        onDelete={handleDelete}
        onReactivated={load}
        readOnly={!canEdit}
      />
    </div>
  );
}
